/**
 *
 * ConfirmEmailChange
 *
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Alert,
  Button,
  FormGroup,
  ControlLabel,
  FormControl,
} from 'react-bootstrap';
import { API2_URL } from 'config/constants';
import { request2 } from 'utils/request';
import getErrorMessage from 'utils/getErrorMessage';
import { useSignOut } from 'containers/User';
import Spinner from 'components/Spinner';
import messages from './messages';

function ConfirmEmailChange({
  user,
  pendingEmail,
  isMerge,
  onConfirmSuccess,
  onCancel,
}) {
  const signOut = useSignOut();
  const [code, setCode] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  function handleConfirm(e) {
    e.preventDefault();

    const submitter = e.nativeEvent && e.nativeEvent.submitter;
    if (submitter && submitter.name === 'cancel') {
      handleCancel();
      return;
    }

    setError(null);
    setConfirming(true);

    request2({
      method: 'POST',
      url: `${API2_URL}/user/confirmChangeEmail`,
      body: { code, newEmail: pendingEmail },
      user,
      // After a lost response a retry gets an error for a change that was
      // made; without it the user sees a network error instead
      retry: false,
    })
      .then(() => {
        setConfirming(false);
        setSuccess(true);
        setTimeout(() => {
          if (onConfirmSuccess) {
            onConfirmSuccess();
          }
          signOut();
        }, 1500);
      })
      .catch(err => {
        setConfirming(false);
        getErrorMessage(err, 'Failed to confirm email change').then(setError);
      });
  }

  function handleCancel() {
    setCanceling(true);
    setError(null);

    request2({
      method: 'DELETE',
      url: `${API2_URL}/user/requestChangeEmail`,
      user,
    })
      .then(() => {
        setCanceling(false);
        if (onCancel) {
          onCancel();
        }
      })
      .catch(err => {
        setCanceling(false);
        getErrorMessage(err, 'Failed to cancel email change').then(setError);
      });
  }

  const isValidCode = /^\d{6}$/.test(code);

  if (success) {
    return (
      <Alert bsStyle="success" className="mt-3">
        <FormattedMessage
          {...(isMerge ? messages.accountsMerged : messages.emailChangeSuccess)}
        />
      </Alert>
    );
  }

  return (
    <div
      className="mt-3 p-3"
      style={{ border: '1px solid #ddd', borderRadius: '4px' }}
    >
      <h5>
        <FormattedMessage {...messages.pendingEmailChange} />
      </h5>
      <Alert bsStyle="info">
        <FormattedMessage
          {...messages.confirmEmailInfo}
          values={{ email: <strong>{pendingEmail}</strong> }}
        />
      </Alert>
      {error && <Alert bsStyle="danger">{error}</Alert>}
      <form onSubmit={handleConfirm}>
        <FormGroup>
          <ControlLabel>
            <FormattedMessage {...messages.confirmationCodeLabel} />
          </ControlLabel>
          <FormControl
            type="text"
            value={code}
            onChange={e =>
              setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
            }
            placeholder="123456"
            maxLength="6"
            disabled={confirming || canceling}
          />
        </FormGroup>
        <div className="text-right mt-3">
          <Button
            bsStyle="default"
            type="submit"
            name="cancel"
            disabled={confirming || canceling}
            className="mr-2"
          >
            {canceling ? (
              <Spinner />
            ) : (
              <FormattedMessage {...messages.cancelEmailChange} />
            )}
          </Button>
          <Button
            bsStyle="primary"
            type="submit"
            disabled={!isValidCode || confirming || canceling}
          >
            {confirming ? (
              <Spinner />
            ) : (
              <FormattedMessage {...messages.confirmChange} />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

ConfirmEmailChange.propTypes = {
  user: PropTypes.object.isRequired,
  pendingEmail: PropTypes.string.isRequired,
  isMerge: PropTypes.bool,
  onConfirmSuccess: PropTypes.func,
  onCancel: PropTypes.func,
};

export default ConfirmEmailChange;
